<template>
    <div v-if="isDataReady" class="container">
        <v-title>User Profile</v-title>
        <v-form-tile>
            <h2 slot="title">Profile Summary</h2>
            <p slot="description" class="text-muted">Overview of the user information.</p>
            <profile-summary @input="updatePassword" slot="content" :profile="profile"/>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Currency and Time Zone</h2>
            <p slot="description" class="text-muted stack-m">USD is selected by default, but you can change it. The selected currency will be used through the entire application.</p>
            <p slot="description" class="text-muted">The interface time zone defines how dates and time will be displayed in the application.</p>
            <currency-timezones
                @input="updateProfile"
                slot="content"
                :profile="profile"
                :is-profile-updating="isProfileUpdating"/>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Two Factor Authentication</h2>
            <p slot="description" class="text-muted">Two factor authentication also know as 2FA is a two step verification. The 2FA method is a
                extra layer of security that require the username, password and the verification number.</p>
            <two-factor-authentication
                @input="saveProfileTwoFactor"
                @generate-secret-key="generateSecretKey"
                slot="content"
                :profile="profile"
                :is-profile-updating="isProfileUpdating"/>
        </v-form-tile>
    </div>
</template>

<script>
    import vTitle from '@/components/v-title';
    import {
        vErrorMessages, vFormTile, vSelect,
    } from '@/components/form';
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import ProfileModel from '@/common/models/profile-model';
    import TwoFactorAuthentication from './two-factor-authentication';
    import ProfileSummary from './profile-summary';

    export default {
        name: 'profile',
        mixins: [LoadStateMixin],
        components: {
            vFormTile,
            vErrorMessages,
            vSelect,
            vTitle,
            CurrencyTimezones: () => import('./currency-timezones'),
            TwoFactorAuthentication,
            ProfileSummary,
        },
        data() {
            return {
                profile: null,
                isProfileUpdating: false,
            };
        },
        methods: {
            async updateProfileAPI(profileAttributes) {
                const data = new ProfileModel({...this.profile, ...profileAttributes});
                const {fields} = await this.$api(({profile}) => profile.update({data}));
                return new ProfileModel(fields);
            },
            async generateSecretKeyAPI() {
                const {fields} = await this.$api(({profile}) => profile.resetTotp());
                this.profile = new ProfileModel(fields);
            },
            async updateProfile(profileAttributes) {
                this.isProfileUpdating = true;
                try {
                    this.profile = await this.updateProfileAPI(profileAttributes);
                    this.populateUserPreferences(this.profile.preferences);
                    this.$toast.positive(`User profile successfully updated`);
                } catch (error) {
                    this.$toast.fromError(error, `Unable to update user profile`);
                } finally {
                    this.isProfileUpdating = false;
                }
            },
            async updatePassword(passwordAttributes) {
                this.isProfileUpdating = true;
                const params = {
                    data: passwordAttributes,
                };
                try {
                    const {fields} = await this.$api(({profile}) => profile.updatePassword(params));
                    this.profile = new ProfileModel(fields);
                    this.$toast.positive(`Password successfully changed`);
                } catch (error) {
                    this.$toast.fromError(error);
                } finally {
                    this.isProfileUpdating = false;
                }
            },
            async generateSecretKey() {
                this.isProfileUpdating = true;
                try {
                    await this.generateSecretKeyAPI();
                } catch (error) {
                    this.$toast.fromError(error);
                } finally {
                    this.isProfileUpdating = false;
                }
            },
            async saveProfileTwoFactor(profileAttributes) {
                this.isProfileUpdating = true;
                try {
                    const profile = await this.updateProfileAPI(profileAttributes);
                    this.profile = profile;
                    this.$toast.positive(`Two factor authentication ${profile.totpRequired ? 'enabled' : 'disabled'}`);
                } catch (error) {
                    this.$toast.fromError(error);
                } finally {
                    this.isProfileUpdating = false;
                }
            },
            async fetchData() {
                try {
                    const response = await this.$api(({profile}) => profile.get());
                    this.profile = new ProfileModel(response.getJSON().fields);
                } catch (error) {
                    this.$toast.negative(`Unable to retrieve user profile`);
                    this.$redirectOnError(error, {path: '/'});
                }
            },
        },
    };
</script>
